import { IDentalData } from '@dataTypes/chartingFeature.types'
import {
  IPhaseItems,
  IPhases,
  ITreatmentPlan,
  TX_PLAN_STATUS,
} from '@dataTypes/treatmentPlan.types'
import { extractDentalConditionsFromTreatmentPlanItems } from '@utils/treatment-plan.utils'

export interface TxPlanPhaseAutoCompleteTarget {
  tp_id: string
  phase_id: string | null
  phase: IPhases
}

const normalizeToothNumber = (toothNumber: string | number): number => {
  const parsed = parseInt(String(toothNumber).trim(), 10)
  return Number.isFinite(parsed) ? parsed : -1
}

const normalizeNullableString = (
  value: string | null | undefined,
): string | null => value ?? null

const dentalDataEntriesMatch = (
  expected: IDentalData,
  actual: IDentalData,
): boolean =>
  normalizeToothNumber(expected.tooth_number) ===
    normalizeToothNumber(actual.tooth_number) &&
  expected.condition === actual.condition &&
  normalizeNullableString(expected.surface) ===
    normalizeNullableString(actual.surface) &&
  normalizeNullableString(expected.type) ===
    normalizeNullableString(actual.type) &&
  normalizeNullableString(expected.material) ===
    normalizeNullableString(actual.material)

export const isPhaseItemCharted = (
  item: IPhaseItems,
  dentalData: IDentalData[],
): boolean => {
  const expectedEntries = extractDentalConditionsFromTreatmentPlanItems([item])
  if (expectedEntries.length === 0) {
    return false
  }
  return expectedEntries.every((expected) =>
    dentalData.some((actual) => dentalDataEntriesMatch(expected, actual)),
  )
}

const getVisiblePhaseItems = (phase: IPhases): IPhaseItems[] =>
  (phase.items ?? []).filter((item) => !item.is_hidden)

const isPhaseEligibleForAutoComplete = (phase: IPhases): boolean => {
  if (phase.is_hidden) {
    return false
  }
  if (phase.status === TX_PLAN_STATUS.COMPLETED) {
    return false
  }
  if (phase.status === TX_PLAN_STATUS.REJECTED) {
    return false
  }
  return phase.status === TX_PLAN_STATUS.ACCEPTED
}

export const getPhasesToAutoComplete = (
  treatmentPlans: ITreatmentPlan[],
  patientId: string,
  dentalData: IDentalData[],
): TxPlanPhaseAutoCompleteTarget[] => {
  if (!patientId || dentalData.length === 0) {
    return []
  }
  const targets: TxPlanPhaseAutoCompleteTarget[] = []
  for (const plan of treatmentPlans) {
    if (plan.patient_id !== patientId) {
      continue
    }
    for (const phase of plan.phases ?? []) {
      if (!isPhaseEligibleForAutoComplete(phase)) {
        continue
      }
      const visibleItems = getVisiblePhaseItems(phase)
      if (visibleItems.length === 0) {
        continue
      }
      const allItemsCharted = visibleItems.every((item) =>
        isPhaseItemCharted(item, dentalData),
      )
      if (!allItemsCharted) {
        continue
      }
      targets.push({
        tp_id: plan.tp_id,
        phase_id: phase.phase_id ?? null,
        phase,
      })
    }
  }
  return targets
}
